import { useEffect, useRef, useState } from "react";
import PolicyModal from "./PolicyModal";
import { asset } from "../../assetUrl";
import "./Template5.css";
import { BRAND_DEFAULT_SVG } from "../../brandDefaultLogo";

/* Default brand logo (shown until the creator sets their own) + default course
   cover image. Both are overridden by data when the creator uploads one. */
// Course-landing pages are light-only (no dark: classes anywhere in this
// folder), so the light placeholder is the right one. The old default was
// FlexiLogo.svg — OUR mark, on a CUSTOMER's sales page, which contradicts the
// rule stated on BrandLogo — and it was a 100 KB "SVG" that turned out to be a
// single <rect> filled with a base64 PNG, so it could not be recoloured either.
const DEFAULT_LOGO = BRAND_DEFAULT_SVG;
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");

/* ──────────────────────────────────────────────────────────────────────────
   Template 5 — "Terminal / Lime on Coal"
   Pixel-exact JSX port of resources/views/fe/courselanding5.blade.php.
   All visual CSS lives in Template5.css scoped under `.clp5-root`. Bootstrap
   (grid + icons) is not loaded in this app, so the grid is reproduced in CSS
   and every `bi bi-*` icon is an inline SVG below.
   ────────────────────────────────────────────────────────────────────────── */

/* ── Inline SVG icons (Bootstrap Icons equivalents) ───────────────────────── */
const Icon = {
  mortarboardFill: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8.211 2.047a.5.5 0 0 0-.422 0l-7.5 3.5a.5.5 0 0 0 .025.917l7.5 3a.5.5 0 0 0 .372 0L14 7.14V13a1 1 0 0 0-1 1v2h3v-2a1 1 0 0 0-1-1V6.739l.686-.275a.5.5 0 0 0 .025-.917l-7.5-3.5Z" />
      <path d="M4.176 9.032a.5.5 0 0 0-.656.327l-.5 1.7a.5.5 0 0 0 .294.605l4.5 1.8a.5.5 0 0 0 .372 0l4.5-1.8a.5.5 0 0 0 .294-.605l-.5-1.7a.5.5 0 0 0-.656-.327L8 10.466 4.176 9.032Z" />
    </svg>
  ),
  book: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z" />
    </svg>
  ),
  playCircle: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z" />
    </svg>
  ),
  playCircleFill: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z" />
    </svg>
  ),
  playFill: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
    </svg>
  ),
  fileEarmarkText: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z" />
      <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
    </svg>
  ),
  check2: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  dot: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
    </svg>
  ),
  chevronDown: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
    </svg>
  ),
  plus: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
    </svg>
  ),
  shieldCheck: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.30 8.531 1.1 8 1.1c-.531 0-1.552.2-2.662.49zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.379 10.485-.004 7.167.592 2.692A1.54 1.54 0 0 1 1.636 1.43a62.468 62.468 0 0 1 2.887-.87z" />
      <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  twitterX: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
    </svg>
  ),
  youtube: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
    </svg>
  ),
  linkedin: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
    </svg>
  ),
  instagram: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
    </svg>
  ),
  facebook: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
    </svg>
  ),
};

/* ── Headline word-split helpers (guruai parity). ─────────────────────────── */
function splitWords(text) {
  return String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

const initials2 = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join("");

const stars = (rating, max = 5) => {
  const r = Math.floor(Number(rating) || 0);
  return Array.from({ length: max }, (_, i) => (i < r ? "★" : "☆")).join("");
};

export default function Template5({ data }) {
  const {
    course = {},
    logo = null,
    header = {},
    skill = {},
    course_include = {},
    prerequisites = {},
    testimonial = {},
    money_back = {},
    faq = {},
    social = {},
    policies = {},
    labels = {},
    video = {},
    video_embed = null,
    modules = [],
    lessonsByModule = {},
    moduleCount = 0,
    lessonCount = 0,
    instructor = null,
    rating = 0,
    enrollText = "Enroll now",
    enrollLink = "#",
  } = data || {};

  const lbl = (k, d) =>
    String(labels?.[k] || "").trim() !== "" ? labels[k] : d;

  const courseName = course.name || course.title || "";
  const heroTitle =
    String(header.headline || "").trim() !== ""
      ? header.headline
      : courseName;
  const heroSub =
    String(header.sub_headline || "").trim() !== ""
      ? header.sub_headline
      : course.short_description || "";
  // Nav brand logo: creator's logo when set, else the default brand logo.
  const logoImg = String(logo || "").trim() !== "" ? logo : DEFAULT_LOGO;
  // Course cover (hero panel): uploaded image, else default.
  const coverImg = course.funnel_page_image || DEFAULT_COURSE_IMAGE;

  const lessonsFor = (cid) => lessonsByModule?.[String(cid)] || [];
  const lessonsCountFor = (cid) => lessonsFor(cid).length;

  // "Course includes" toggle gates the curriculum section (default ON).
  const showCurriculum =
    course_include.status === undefined ? true : !!course_include.status;

  // Hero / CTA headline word-splits (guruai parity).
  const hWords = splitWords(heroTitle);
  const hLine1 = hWords[0] || "";
  const hW1 = hWords[1] || "";
  const hW2 = hWords[2] || "";
  const hLine3 = hWords.slice(3).join(" ");

  const cWords = splitWords(heroTitle);
  const cLine1 = cWords[0] || "";
  const cW1 = cWords[1];
  const cLine3 = cWords.slice(2).join(" ");

  const headerVisible = header.status === undefined || header.status;

  // ── Curriculum accordion: first module open by default. ──
  const [openModule, setOpenModule] = useState(0);
  // ── FAQ accordion. ──
  const [openFaq, setOpenFaq] = useState(null);
  // ── Footer policy popup: "privacy" | "terms" | null ──
  const [openPolicy, setOpenPolicy] = useState(null);
  const hasPrivacy = String(policies?.privacy || "").trim() !== "";
  const hasTerms = String(policies?.terms || "").trim() !== "";
  // ── Hero video modal. ──
  const [videoPlaying, setVideoPlaying] = useState(false);
  const iframeSrc = (() => {
    if (!video_embed) return null;
    if ((video.video_type || "") === "other") return video_embed;
    const sep = video_embed.indexOf("?") !== -1 ? "&" : "?";
    return `${video_embed}${sep}enablejsapi=1`;
  })();
  const autoplaySrc = (() => {
    if (!iframeSrc) return null;
    if (iframeSrc.indexOf("autoplay=1") !== -1) return iframeSrc;
    const sep = iframeSrc.indexOf("?") !== -1 ? "&" : "?";
    return `${iframeSrc}${sep}autoplay=1`;
  })();

  // ── Scroll reveal (IntersectionObserver) + scroll progress bar. ──
  const rootRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("in");
        });
      },
      { threshold: 0.07 }
    );
    root.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    const onScroll = () => {
      const pct =
        (window.scrollY /
          (document.body.scrollHeight - window.innerHeight)) *
        100;
      setProgress(Number.isFinite(pct) ? pct : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [data]);

  // Smooth in-page scroll.
  const onAnchorClick = (e, href) => {
    const target = rootRef.current?.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const heroStyle = header.header_bg_image
    ? {
        backgroundImage: `url('${header.header_bg_image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  const guaranteeStyle = money_back.money_back_bg_image
    ? {
        backgroundImage: `url('${money_back.money_back_bg_image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  // Guarantee defaults (parity with blade).
  const mbStatus = money_back?.status === undefined ? 1 : money_back.status;
  const mbDescriptionRaw =
    String(money_back.description || "").trim() !== "";
  const mbHeading = mbDescriptionRaw || String(money_back.heading || "").trim() !== ""
    ? (String(money_back.heading || "").trim() !== ""
        ? money_back.heading
        : "30-Day Risk-Free Money-Back Guarantee")
    : "30-Day Risk-Free Money-Back Guarantee";
  const mbDescription = mbDescriptionRaw
    ? money_back.description
    : "We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full 30-day risk-free money-back guarantee. <br><br>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first 30 days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.<br><br>We'll process your refund promptly—no questions asked.<br><br>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.";

  // Guarantee headline word-split.
  const gWords = splitWords(mbHeading);
  const gLine1 = gWords[0] || "";
  const gW1 = gWords[1] || "";
  const gW2 = gWords[2] || "";
  const gLine3 = gWords.slice(3).join(" ");

  const hasLearnBlock =
    (skill?.status && skill?.list?.length) ||
    (course_include?.status && course_include?.list?.length) ||
    (prerequisites?.status && prerequisites?.list?.length);

  // FAQ split into two columns (even / odd indices), matching blade.
  const faqList = faq?.list || [];
  const faqLeft = faqList
    .map((f, i) => ({ f, i }))
    .filter(({ i }) => i % 2 === 0);
  const faqRight = faqList
    .map((f, i) => ({ f, i }))
    .filter(({ i }) => i % 2 !== 0);

  const renderFaqItem = ({ f, i }) => {
    const isOpen = openFaq === i;
    return (
      <div className="fitem" key={i}>
        <button
          className="fbtn"
          aria-expanded={isOpen ? "true" : "false"}
          onClick={() => setOpenFaq(isOpen ? null : i)}
          type="button"
        >
          <span className="fq">{f.question || ""}</span>
          <Icon.plus className={`fico${isOpen ? " rotate" : ""}`} />
        </button>
        {isOpen ? (
          <div>
            <div className="fbody">{f.answer || ""}</div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="clp5-root" ref={rootRef}>
      <div id="rbar" style={{ width: `${progress}%` }} />

      {/* ══ NAVBAR ══ */}
      <nav className="nav-w">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between">
            <a
              href="#top"
              className="brand"
              onClick={(e) => onAnchorClick(e, "#top")}
            >
              <img src={logoImg} className="site-logo" alt="Logo" />
            </a>
            <div className="d-none d-md-flex align-items-center gap-1">
              {moduleCount && showCurriculum ? (
                <a
                  href="#curriculum"
                  className="nl"
                  onClick={(e) => onAnchorClick(e, "#curriculum")}
                >
                  {lbl("curriculum", "Curriculum")}
                </a>
              ) : null}
              {instructor && instructor.name ? (
                <a
                  href="#instructor"
                  className="nl"
                  onClick={(e) => onAnchorClick(e, "#instructor")}
                >
                  {lbl("instructor", "Instructor")}
                </a>
              ) : null}
              {testimonial?.status && testimonial?.list?.length ? (
                <a
                  href="#reviews"
                  className="nl"
                  onClick={(e) => onAnchorClick(e, "#reviews")}
                >
                  {lbl("reviews", "Reviews")}
                </a>
              ) : null}
              {faq?.status && faq?.list?.length ? (
                <a
                  href="#faq"
                  className="nl"
                  onClick={(e) => onAnchorClick(e, "#faq")}
                >
                  FAQ
                </a>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-3">
              <a href={enrollLink} className="btn-nav">
                <Icon.mortarboardFill className="me-2" />
                {enrollText}
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ══ HERO ══ */}
      <section className="hero" id="top" style={heroStyle}>
        <div className="hero-noise" />
        <div className="hero-lime-line" />
        <div className="hero-glow" />
        <div className="container">
          <div className="row align-items-center g-5">
            {/* LEFT */}
            <div className="col-lg-7">
              <h1 className="hero-title fu d1">
                {hLine1}
                <br />
                {hW1 !== "" || hW2 !== "" ? (
                  <>
                    <span className="li">{hW1}</span>{" "}
                    <span className="outline">{hW2}</span>
                    <br />
                  </>
                ) : null}
                {hLine3}
              </h1>
              {heroSub ? <p className="hero-sub fu d2">{heroSub}</p> : null}
              <div className="hero-btns fu d3">
                <a href={enrollLink} className="btn-hp">
                  <Icon.mortarboardFill /> {enrollText}
                </a>
                {moduleCount && showCurriculum ? (
                  <a
                    href="#curriculum"
                    className="btn-hg"
                    onClick={(e) => onAnchorClick(e, "#curriculum")}
                  >
                    <Icon.playCircle /> {lbl("preview_course", "Preview Course")}
                  </a>
                ) : null}
              </div>
              {rating > 0 ? (
                <div className="proof-row fu d4">
                  <div className="pb d-flex align-items-center">
                    <span style={{ color: "#fbbf24", fontSize: ".82rem" }}>
                      {stars(rating)}
                    </span>
                    <span>
                      &nbsp;<strong>{Number(rating).toFixed(1)}</strong>/5
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {/* RIGHT: Dashboard panel */}
            {headerVisible ? (
              <div className="col-lg-5 fu d2">
                <div className="hero-panel">
                  <div className="hp-bar">
                    <div className="hpdot" style={{ background: "#f87171" }} />
                    <div className="hpdot" style={{ background: "#fbbf24" }} />
                    <div className="hpdot" style={{ background: "var(--li)" }} />
                  </div>
                  <div className="hp-body">
                    {video_embed ? (
                      <div className="hp-media">
                        {!videoPlaying ? (
                          <div
                            className="video_thumb5"
                            onClick={() => setVideoPlaying(true)}
                          >
                            <img src={video.image || coverImg} alt="" />
                            <div className="play-btn5">
                              <Icon.playFill />
                            </div>
                          </div>
                        ) : (
                          <div className="video_iframe5">
                            {(video.video_type || "") === "other" ? (
                              <video controls autoPlay>
                                <source src={video_embed} type="video/mp4" />
                              </video>
                            ) : (
                              <iframe
                                title="course-video"
                                src={autoplaySrc}
                                frameBorder="0"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="hp-media">
                        <img src={coverImg} alt="" />
                      </div>
                    )}

                    {modules.length ? (
                      <div className="hp-section mt-3">
                        <div className="hp-lessons">
                          {modules.slice(0, 4).map((m) => (
                            <div className="hp-lesson on" key={m.course_id}>
                              <div className="hp-lesson-ico">
                                <Icon.book />
                              </div>
                              <span style={{ flex: 1, fontSize: ".77rem" }}>
                                {m.course_name}
                              </span>
                              <span className="hp-lesson-dur">
                                {lessonsCountFor(m.course_id)}{" "}
                                {lbl("lessons", "lessons")}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* ══ WHAT YOU'LL LEARN / INCLUDES / PREREQUISITES ══ */}
      {hasLearnBlock ? (
        <div className="container py-5">
          <div className="row g-5">
            <div className="col-lg-12">
              {skill?.status && skill?.list?.length ? (
                <section className="csec pt-0" id="learn">
                  <div className="ey reveal">
                    {lbl("what_youll_learn", "What You'll Learn.")}
                  </div>
                  <h2 className="sh mb-4 reveal">
                    {skill.heading || "What you'll learn"}
                  </h2>
                  <div className="lgrid reveal">
                    {skill.list.map((s, i) => (
                      <div className="litem" key={i}>
                        <i>
                          <Icon.check2 />
                        </i>
                        {s.pts || ""}
                      </div>
                    ))}
                  </div>
                  {skill.long_desc ? (
                    <div className="skill-long reveal">
                      {String(skill.long_desc)
                        .split("\n")
                        .map((line, i, arr) => (
                          <span key={i}>
                            {line}
                            {i < arr.length - 1 ? <br /> : null}
                          </span>
                        ))}
                    </div>
                  ) : null}
                </section>
              ) : null}

              {course_include?.status && course_include?.list?.length ? (
                <section className="csec" id="includes">
                  <div className="ey reveal">
                    {lbl("what_inside_course", "What's inside")}
                  </div>
                  <h2 className="sh mb-4 reveal">
                    {course_include.heading || "This course includes"}
                  </h2>
                  <div className="lgrid reveal">
                    {course_include.list.map((c, i) => (
                      <div className="litem" key={i}>
                        <i>
                          <Icon.check2 />
                        </i>
                        {c.pts || ""}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {prerequisites?.status && prerequisites?.list?.length ? (
                <section className="csec" id="requirements">
                  <div className="ey reveal">
                    {lbl("prerequisites", "Prerequisites")}
                  </div>
                  <h2 className="sh mb-4 reveal">
                    {prerequisites.heading || "Prerequisites"}
                  </h2>
                  <div className="lgrid reveal">
                    {prerequisites.list.map((p, i) => (
                      <div className="litem" key={i}>
                        <i style={{ fontSize: "1.1rem" }}>
                          <Icon.dot />
                        </i>
                        {p.pts || ""}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* ══ CURRICULUM ══ */}
      {moduleCount && showCurriculum ? (
        <section className="curr-sec" id="curriculum">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-8">
                <div className="ey reveal">{lbl("curriculum", "Curriculum")}</div>
                <h2 className="sh reveal">
                  {lbl("inside_course", "What's inside the course")}
                </h2>
                <p
                  className="reveal mt-1"
                  style={{
                    fontSize: ".75rem",
                    color: "var(--fn)",
                    fontFamily: "'Fira Mono', monospace",
                  }}
                >
                  {moduleCount} {lbl("modules", "modules")} · {lessonCount}{" "}
                  {lbl("lessons", "lessons")}
                </p>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-8 reveal">
                <div className="curr-list">
                  {modules.map((m, idx) => {
                    const mls = lessonsFor(m.course_id);
                    const isOpen = openModule === idx;
                    return (
                      <div className="citem" key={m.course_id}>
                        <button
                          className={`chdr${isOpen ? "" : " collapsed"}`}
                          aria-expanded={isOpen ? "true" : "false"}
                          onClick={() => setOpenModule(isOpen ? -1 : idx)}
                          type="button"
                        >
                          <span className="cnum">
                            {String(idx + 1).padStart(2, "0")}
                          </span>
                          <span className="ctitle">{m.course_name}</span>
                          <span className="cmeta">
                            {mls.length} {lbl("lessons", "lessons")}
                          </span>
                          <Icon.chevronDown className="cchev" />
                        </button>
                        {isOpen ? (
                          <div>
                            <div className="cbody">
                              {mls.length ? (
                                mls.map((child) => (
                                  <div className="lrow" key={child.lesson_id}>
                                    {Number(child.lesson_type || 0) === 0 ? (
                                      <i
                                        className="lico"
                                        style={{ color: "var(--li)" }}
                                      >
                                        <Icon.fileEarmarkText />
                                      </i>
                                    ) : (
                                      <i
                                        className="lico"
                                        style={{ color: "var(--li)" }}
                                      >
                                        <Icon.playCircleFill />
                                      </i>
                                    )}
                                    <span style={{ flex: 1 }}>
                                      {String(
                                        child.lesson_name || "Lesson"
                                      ).slice(0, 80)}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <div
                                  className="text-center py-3"
                                  style={{ textAlign: "center" }}
                                >
                                  <small style={{ color: "var(--mu)" }}>
                                    {lbl("no_lesson_found", "No lessons found")}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ══ INSTRUCTOR ══ */}
      {instructor && instructor.name ? (
        <section className="csec" id="instructor">
          <div className="container">
            <div className="ey reveal">{lbl("instructor", "Instructor")}</div>
            <h2 className="sh mb-4 reveal">
              {lbl("meet_instructor", "Meet the Instructor")}
            </h2>
            <div className="inst-card reveal">
              {instructor.profile_image ? (
                <img
                  src={instructor.profile_image}
                  className="inst-av"
                  alt="Instructor"
                />
              ) : (
                <div className="inst-av">{initials2(instructor.name)}</div>
              )}
              <div>
                <div className="inst-name">{instructor.name}</div>
                {instructor.location ? (
                  <div className="inst-role">{instructor.location}</div>
                ) : null}
                <p className="inst-bio">{instructor.short_description || ""}</p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ GUARANTEE ▓ */}
      {mbStatus == 1 ? (
        <section className="guarantee-sec" id="guarantee" style={guaranteeStyle}>
          <div className="container">
            <div className="row align-items-center guar-row">
              <div className="col-lg-6">
                <div className="guar-inner text-lg-start text-center">
                  <h1 className="guar-title fu d1">
                    {gLine1}
                    <br />
                    {gW1 !== "" || gW2 !== "" ? (
                      <>
                        <span className="li">{gW1}</span>{" "}
                        <span className="outline">{gW2}</span>
                        <br />
                      </>
                    ) : null}
                    {gLine3}
                  </h1>
                  <p
                    className="guar-text"
                    dangerouslySetInnerHTML={{ __html: mbDescription }}
                  />
                </div>
              </div>
              <div className="col-lg-6 text-center text-lg-end">
                {money_back.money_back_image ? (
                  <img
                    src={money_back.money_back_image}
                    alt="Guarantee Image"
                    className="img-fluid rounded guar-img"
                  />
                ) : (
                  <div className="guar-img">
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 427 427"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      xmlSpace="preserve"
                      style={{
                        fillRule: "evenodd",
                        clipRule: "evenodd",
                        strokeLinejoin: "round",
                        strokeMiterlimit: 2,
                      }}
                    >
                      <g transform="matrix(1,0,0,1,-1563.289163,-413.899927)">
                        <g transform="matrix(0.584335,0,0,0.584335,1465.927673,317.484615)">
                          <path
                            d="M531.461,165L573.906,207.444L625.89,177.432L655.902,229.415L713.883,213.88L729.418,271.86L789.444,271.86L789.444,331.885L847.424,347.421L831.888,405.401L883.872,435.414L853.859,487.398L896.304,529.842L853.859,572.287L883.872,624.27L831.888,654.283L847.424,712.263L789.444,727.799L789.444,787.825L729.418,787.825L713.883,845.805L655.902,830.269L625.89,882.253L573.906,852.24L531.461,894.684L489.017,852.24L437.033,882.253L407.021,830.269L349.04,845.805L333.505,787.825L273.479,787.825L273.479,727.799L215.499,712.263L231.035,654.283L179.051,624.27L209.064,572.287L166.619,529.842L209.064,487.398L179.051,435.414L231.035,405.401L215.499,347.421L273.479,331.885L273.479,271.86L333.505,271.86L349.04,213.88L407.021,229.415L437.033,177.432L489.017,207.444L531.461,165ZM531.461,178.005L490.53,218.937L440.399,189.994L411.456,240.125L355.543,225.143L340.561,281.056L282.675,281.056L282.675,338.942L226.762,353.924L241.744,409.837L191.613,438.78L220.556,488.911L179.625,529.842L220.556,570.774L191.613,620.904L241.744,649.847L226.762,705.761L282.675,720.743L282.675,778.628L340.561,778.628L355.543,834.542L411.456,819.56L440.399,869.691L490.53,840.748L531.461,881.679L572.393,840.748L622.524,869.691L651.466,819.56L707.38,834.542L722.362,778.628L780.248,778.628L780.248,720.743L836.161,705.761L821.179,649.847L871.31,620.904L842.367,570.774L883.298,529.842L842.367,488.911L871.31,438.78L821.179,409.837L836.161,353.924L780.248,338.942L780.248,281.056L722.362,281.056L707.38,225.143L651.466,240.125L622.524,189.994L572.393,218.937L531.461,178.005ZM531.461,219.283C702.864,219.283 842.021,358.44 842.021,529.842C842.021,701.244 702.864,840.401 531.461,840.401C360.059,840.401 220.902,701.244 220.902,529.842C220.902,358.44 360.059,219.283 531.461,219.283ZM531.461,348.133C431.173,348.133 349.752,429.554 349.752,529.842C349.752,630.13 431.173,711.551 531.461,711.551C631.749,711.551 713.171,630.13 713.171,529.842C713.171,429.554 631.749,348.133 531.461,348.133ZM612.965,638.415C607.679,638.415 603.275,637.446 599.751,635.508C596.227,633.57 593.567,630.646 591.77,626.734C589.973,622.823 588.933,617.837 588.651,611.776L603.662,609.239C603.803,612.763 604.173,615.705 604.772,618.066C605.371,620.427 606.288,622.189 607.521,623.352C608.754,624.514 610.287,625.096 612.119,625.096C614.375,625.096 615.907,624.391 616.718,622.982C617.528,621.572 617.933,619.951 617.933,618.119C617.933,614.525 617.07,611.512 615.344,609.08C613.617,606.649 611.344,604.2 608.525,601.734L599.645,594.017C596.474,591.339 593.884,588.308 591.876,584.925C589.867,581.543 588.863,577.385 588.863,572.451C588.863,565.404 590.924,559.995 595.047,556.225C599.17,552.454 604.825,550.569 612.014,550.569C616.313,550.569 619.836,551.274 622.585,552.683C625.333,554.093 627.465,555.978 628.98,558.339C630.496,560.7 631.553,563.272 632.152,566.056C632.751,568.84 633.121,571.606 633.262,574.354L618.356,576.574C618.215,573.967 617.986,571.676 617.669,569.703C617.352,567.73 616.718,566.179 615.766,565.052C614.815,563.924 613.388,563.36 611.485,563.36C609.441,563.36 607.926,564.118 606.939,565.633C605.953,567.148 605.46,568.822 605.46,570.654C605.46,573.685 606.147,576.169 607.521,578.107C608.895,580.045 610.78,582.071 613.176,584.185L621.845,591.797C625.439,594.898 628.522,598.439 631.095,602.421C633.667,606.402 634.953,611.318 634.953,617.167C634.953,621.184 634.037,624.814 632.205,628.056C630.372,631.298 627.818,633.835 624.54,635.667C621.263,637.499 617.405,638.415 612.965,638.415ZM495.515,536.936C488.034,536.936 482.101,535.528 477.714,532.711C473.328,529.894 470.165,525.923 468.226,520.798C466.286,515.673 465.317,509.785 465.317,503.136L465.317,499.119L486.649,499.119L486.649,502.859C486.742,506.461 487.042,509.439 487.55,511.794C488.058,514.149 488.958,515.927 490.251,517.127C491.544,518.328 493.391,518.928 495.792,518.928C498.285,518.928 500.132,518.305 501.333,517.058C502.533,515.811 503.364,513.987 503.826,511.586C504.288,509.185 504.519,506.23 504.519,502.721C504.519,497.087 503.688,492.724 502.025,489.63C500.363,486.537 497.038,484.944 492.052,484.851L488.311,484.851L488.311,469.06L491.636,469.06C496.623,469.06 500.017,467.72 501.817,465.042C503.618,462.364 504.519,458.162 504.519,452.437C504.519,448.004 503.872,444.541 502.579,442.048C501.286,439.554 498.885,438.307 495.376,438.307C491.867,438.307 489.558,439.693 488.45,442.463C487.342,445.234 486.742,448.789 486.649,453.129L486.649,458.116L465.317,458.116L465.317,452.021C465.317,445.464 466.517,439.9 468.918,435.329C471.319,430.758 474.736,427.272 479.169,424.871C483.602,422.47 489.004,421.269 495.376,421.269C501.748,421.269 507.197,422.423 511.722,424.732C516.247,427.041 519.687,430.412 522.042,434.844C524.397,439.277 525.574,444.772 525.574,451.329C525.574,457.885 524.212,463.334 521.488,467.674C518.764,472.015 515.416,474.785 511.445,475.986C514.215,476.909 516.663,478.433 518.787,480.557C520.911,482.681 522.573,485.521 523.773,489.076C524.974,492.632 525.574,497.041 525.574,502.305C525.574,509.047 524.605,515.026 522.665,520.244C520.726,525.462 517.54,529.548 513.107,532.503C508.674,535.458 502.81,536.936 495.515,536.936ZM427.97,637.253L427.97,551.626L448.901,551.626C456.16,551.626 461.815,552.56 465.867,554.428C469.92,556.295 472.792,559.22 474.483,563.202C476.174,567.183 477.02,572.275 477.02,578.477L477.02,609.662C477.02,616.005 476.174,621.22 474.483,625.307C472.792,629.395 469.955,632.407 465.973,634.346C461.991,636.284 456.442,637.253 449.324,637.253L427.97,637.253ZM445.412,625.096L449.112,625.096C452.354,625.096 454.662,624.585 456.036,623.563C457.411,622.541 458.274,621.026 458.626,619.017C458.979,617.009 459.155,614.525 459.155,611.565L459.155,576.257C459.155,573.297 458.926,570.919 458.468,569.122C458.01,567.324 457.093,566.003 455.719,565.157C454.345,564.312 452.107,563.889 449.006,563.889L445.412,563.889L445.412,625.096ZM567.824,536.936C561.082,536.936 555.495,535.551 551.062,532.78C546.63,530.01 543.305,526.2 541.089,521.352C538.872,516.504 537.764,510.894 537.764,504.521L537.764,454.238C537.764,447.681 538.826,441.932 540.95,436.991C543.074,432.051 546.353,428.195 550.785,425.425C555.218,422.654 560.898,421.269 567.824,421.269C574.658,421.269 580.268,422.654 584.654,425.425C589.041,428.195 592.296,432.051 594.42,436.991C596.544,441.932 597.606,447.681 597.606,454.238L597.606,504.521C597.606,510.894 596.498,516.504 594.282,521.352C592.065,526.2 588.741,530.01 584.308,532.78C579.875,535.551 574.381,536.936 567.824,536.936ZM567.824,518.097C570.317,518.097 572.118,517.312 573.226,515.742C574.334,514.172 575.073,512.302 575.443,510.132C575.812,507.961 575.997,505.953 575.997,504.106L575.997,454.653C575.997,452.621 575.835,450.497 575.512,448.281C575.189,446.065 574.473,444.148 573.365,442.532C572.257,440.916 570.41,440.108 567.824,440.108C565.238,440.108 563.368,440.916 562.214,442.532C561.059,444.148 560.32,446.065 559.997,448.281C559.674,450.497 559.512,452.621 559.512,454.653L559.512,504.106C559.512,505.953 559.697,507.961 560.067,510.132C560.436,512.302 561.221,514.172 562.421,515.742C563.622,517.312 565.423,518.097 567.824,518.097ZM550.912,637.253L550.912,603.319L534.21,551.626L550.595,551.626L559.792,581.754L568.355,551.626L584.212,551.626L567.721,603.319L567.721,637.253L550.912,637.253ZM483.151,637.253L499.748,551.626L519.093,551.626L535.584,637.253L519.41,637.253L516.345,618.436L502.814,618.436L499.537,637.253L483.151,637.253ZM504.399,607.759L514.653,607.759L509.473,573.191L504.399,607.759Z"
                            style={{ fill: "var(--green)" }}
                          />
                        </g>
                        <g transform="matrix(2.02948,0,0,2.02948,1288.169365,241.173328)">
                          <path
                            d="M189.283,160.704L173.347,148.233L176.237,144.405L186.966,149.72L179.021,140.717L181.806,137.029L198.164,148.942L196.252,151.474L185.282,143.66L194.612,153.647L192.911,155.9L180.581,149.72L191.225,158.132L189.283,160.704ZM204.19,144.494C203.005,145.439 201.902,145.979 200.88,146.113C199.858,146.247 198.885,146.043 197.962,145.501C197.04,144.96 196.147,144.148 195.285,143.068L190.594,137.192C189.722,136.098 189.129,135.048 188.815,134.041C188.502,133.035 188.526,132.051 188.889,131.09C189.251,130.13 190.025,129.177 191.209,128.231C192.407,127.276 193.517,126.725 194.541,126.579C195.564,126.433 196.532,126.625 197.444,127.154C198.357,127.683 199.249,128.494 200.122,129.587L204.812,135.464C205.674,136.544 206.268,137.594 206.592,138.614C206.916,139.634 206.896,140.63 206.532,141.602C206.168,142.574 205.387,143.538 204.19,144.494ZM202.211,142.015C202.575,141.724 202.769,141.42 202.793,141.102C202.817,140.785 202.74,140.463 202.564,140.135C202.387,139.807 202.18,139.493 201.941,139.194L195.863,131.58C195.614,131.267 195.352,130.992 195.076,130.754C194.801,130.516 194.506,130.373 194.191,130.326C193.877,130.279 193.537,130.4 193.173,130.691C192.834,130.961 192.653,131.255 192.629,131.572C192.606,131.89 192.679,132.209 192.851,132.53C193.022,132.852 193.232,133.169 193.482,133.481L199.559,141.095C199.798,141.394 200.054,141.669 200.328,141.919C200.603,142.169 200.894,142.32 201.202,142.372C201.51,142.425 201.846,142.306 202.211,142.015ZM210.059,139.714L201.216,121.515L204.047,120.139L212.035,127.089L207.777,118.327L211.102,116.711L219.945,134.91L217.227,136.231L208.912,128.607L213.497,138.043L210.059,139.714ZM221.601,134.401L216.585,114.799L225.394,112.545L226.15,115.498L221.673,116.643L222.917,121.507L226.33,120.634L227.097,123.635L223.685,124.508L225.184,130.365L229.709,129.207L230.458,132.135L221.601,134.401ZM233.757,131.799L233.096,123.557L228.305,111.938L232.513,111.6L234.942,117.821L236.149,111.309L240.183,110.985L237.379,123.214L238.04,131.456L233.757,131.799ZM247.349,131.497L251.735,111.745L256.881,112.887C257.726,113.075 258.512,113.318 259.239,113.616C259.966,113.914 260.582,114.31 261.086,114.806C261.59,115.302 261.934,115.924 262.117,116.673C262.301,117.421 262.27,118.349 262.024,119.454C261.847,120.251 261.581,120.895 261.224,121.387C260.867,121.88 260.438,122.24 259.935,122.47C259.431,122.699 258.867,122.804 258.24,122.785C258.982,123.052 259.569,123.442 260.001,123.956C260.433,124.47 260.709,125.09 260.829,125.816C260.949,126.542 260.905,127.377 260.696,128.319C260.468,129.344 260.138,130.196 259.706,130.876C259.273,131.556 258.753,132.076 258.144,132.435C257.535,132.795 256.836,133.007 256.046,133.07C255.255,133.133 254.381,133.058 253.422,132.845L247.349,131.497ZM254.346,120.615L255.2,120.804C255.866,120.952 256.393,120.963 256.779,120.835C257.166,120.708 257.463,120.463 257.672,120.1C257.88,119.737 258.046,119.279 258.169,118.726C258.284,118.206 258.287,117.75 258.178,117.359C258.069,116.968 257.851,116.642 257.525,116.382C257.199,116.122 256.759,115.931 256.207,115.808L255.451,115.64L254.346,120.615ZM252.391,129.418L253.318,129.624C254.277,129.837 254.994,129.74 255.468,129.334C255.942,128.927 256.284,128.252 256.493,127.31C256.659,126.562 256.71,125.95 256.644,125.475C256.579,125 256.382,124.62 256.054,124.333C255.725,124.047 255.243,123.834 254.609,123.693L253.707,123.492L252.391,129.418ZM259.492,134.185L271.468,117.435L276.092,119.578L271.005,139.522L267.266,137.788L268.45,133.629L265.753,132.379L263.322,135.961L259.492,134.185ZM267.24,130.122L269.189,131.026L271.534,123.412L267.24,130.122ZM275.79,143.532C274.566,142.611 273.765,141.659 273.389,140.678C273.013,139.696 272.981,138.693 273.294,137.668C273.608,136.642 274.175,135.584 274.996,134.493L279.383,128.666C280.224,127.548 281.094,126.697 281.993,126.112C282.891,125.528 283.862,125.284 284.904,125.38C285.946,125.477 287.079,125.986 288.303,126.908C289.501,127.809 290.288,128.709 290.664,129.607C291.041,130.505 291.103,131.412 290.852,132.328C290.601,133.243 290.129,134.16 289.438,135.078L288.387,136.475L284.894,133.846L286.111,132.229C286.351,131.91 286.565,131.592 286.752,131.274C286.939,130.956 287.026,130.641 287.011,130.328C286.997,130.014 286.796,129.713 286.411,129.422C286.038,129.142 285.687,129.034 285.357,129.098C285.027,129.163 284.726,129.327 284.454,129.591C284.183,129.855 283.921,130.154 283.671,130.487L277.947,138.09C277.677,138.45 277.459,138.801 277.294,139.146C277.128,139.49 277.068,139.82 277.112,140.135C277.156,140.45 277.352,140.737 277.698,140.998C278.07,141.278 278.409,141.382 278.714,141.309C279.019,141.236 279.306,141.062 279.576,140.786C279.847,140.51 280.102,140.212 280.342,139.893L281.619,138.197L285.112,140.826L284.06,142.223C283.369,143.141 282.614,143.86 281.796,144.379C280.977,144.899 280.074,145.116 279.087,145.029C278.1,144.943 277,144.444 275.79,143.532ZM281.512,147.597L296.981,134.555L299.863,137.973L293.542,143.303L302.053,140.57L304.871,143.912L295.466,146.941L289.515,157.088L286.584,153.613L291.537,145.614L290.62,145.766L284.394,151.015L281.512,147.597Z"
                            style={{ fill: "var(--green-light)" }}
                          />
                        </g>
                        <g transform="matrix(4.327298,0,0,4.327298,1488.316367,-64.84416)">
                          <path
                            d="M41.569,189.436C40.913,189.436 40.393,189.298 40.009,189.022C39.625,188.746 39.351,188.354 39.187,187.846C39.023,187.338 38.941,186.748 38.941,186.076L38.941,182.824C38.941,182.128 39.025,181.528 39.193,181.024C39.361,180.52 39.655,180.132 40.075,179.86C40.495,179.588 41.065,179.452 41.785,179.452C42.481,179.452 43.031,179.568 43.435,179.8C43.839,180.032 44.129,180.36 44.305,180.784C44.481,181.208 44.569,181.712 44.569,182.296L44.569,182.86L42.637,182.86L42.637,182.176C42.637,181.92 42.621,181.694 42.589,181.498C42.557,181.302 42.483,181.148 42.367,181.036C42.251,180.924 42.065,180.868 41.809,180.868C41.545,180.868 41.353,180.936 41.233,181.072C41.113,181.208 41.037,181.382 41.005,181.594C40.973,181.806 40.957,182.032 40.957,182.272L40.957,186.58C40.957,186.852 40.981,187.096 41.029,187.312C41.077,187.528 41.167,187.696 41.299,187.816C41.431,187.936 41.621,187.996 41.869,187.996C42.117,187.996 42.309,187.932 42.445,187.804C42.581,187.676 42.675,187.502 42.727,187.282C42.779,187.062 42.805,186.812 42.805,186.532L42.805,185.5L41.857,185.5L41.857,184.3L44.593,184.3L44.593,189.292L43.297,189.292L43.177,188.416C43.049,188.712 42.857,188.956 42.601,189.148C42.345,189.34 42.001,189.436 41.569,189.436Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M48.685,189.424C47.949,189.424 47.377,189.298 46.969,189.046C46.561,188.794 46.275,188.43 46.111,187.954C45.947,187.478 45.865,186.9 45.865,186.22L45.865,179.572L47.821,179.572L47.821,186.496C47.821,186.736 47.839,186.972 47.875,187.204C47.911,187.436 47.991,187.624 48.115,187.768C48.239,187.912 48.429,187.984 48.685,187.984C48.949,187.984 49.143,187.912 49.267,187.768C49.391,187.624 49.469,187.436 49.501,187.204C49.533,186.972 49.549,186.736 49.549,186.496L49.549,179.572L51.505,179.572L51.505,186.22C51.505,186.9 51.423,187.478 51.259,187.954C51.095,188.43 50.809,188.794 50.401,189.046C49.993,189.298 49.421,189.424 48.685,189.424Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M52.321,189.292L54.205,179.572L56.401,179.572L58.273,189.292L56.437,189.292L56.089,187.156L54.553,187.156L54.181,189.292L52.321,189.292ZM54.733,185.944L55.897,185.944L55.309,182.02L54.733,185.944Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M59.257,189.292L59.257,179.572L61.777,179.572C62.433,179.572 62.995,179.646 63.463,179.794C63.931,179.942 64.291,180.204 64.543,180.58C64.795,180.956 64.921,181.476 64.921,182.14C64.921,182.54 64.887,182.894 64.819,183.202C64.751,183.51 64.631,183.778 64.459,184.006C64.287,184.234 64.045,184.416 63.733,184.552L65.077,189.292L63.049,189.292L61.933,184.888L61.237,184.888L61.237,189.292L59.257,189.292ZM61.237,183.712L61.813,183.712C62.133,183.712 62.385,183.66 62.569,183.556C62.753,183.452 62.883,183.294 62.959,183.082C63.035,182.87 63.073,182.608 63.073,182.296C63.073,181.848 62.991,181.506 62.827,181.27C62.663,181.034 62.357,180.916 61.909,180.916L61.237,180.916L61.237,183.712Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M65.773,189.292L67.657,179.572L69.853,179.572L71.725,189.292L69.889,189.292L69.541,187.156L68.005,187.156L67.633,189.292L65.773,189.292ZM68.185,185.944L69.349,185.944L68.761,182.02L68.185,185.944Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M72.709,189.292L72.709,179.572L74.089,179.572L76.249,184.636L76.249,179.572L77.881,179.572L77.881,189.292L76.561,189.292L74.401,183.916L74.401,189.292L72.709,189.292Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M80.269,189.292L80.269,181.048L78.769,181.048L78.769,179.572L83.737,179.572L83.737,181.048L82.261,181.048L82.261,189.292L80.269,189.292Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M84.613,189.292L84.613,179.572L88.849,179.572L88.849,180.94L86.593,180.94L86.593,183.52L88.333,183.52L88.333,184.9L86.593,184.9L86.593,187.948L88.873,187.948L88.873,189.292L84.613,189.292Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                          <path
                            d="M89.869,189.292L89.869,179.572L94.105,179.572L94.105,180.94L91.849,180.94L91.849,183.52L93.589,183.52L93.589,184.9L91.849,184.9L91.849,187.948L94.129,187.948L94.129,189.292L89.869,189.292Z"
                            style={{ fill: "var(--green)", fillRule: "nonzero" }}
                          />
                        </g>
                        <g transform="matrix(0.704105,0,0,0.704105,1425.834857,276.093569)">
                          <path
                            d="M230.188,688.866L283.781,688.866L283.781,712.544L230.188,688.866ZM765.812,688.866L712.219,712.544L712.219,688.866L765.812,688.866Z"
                            style={{ fill: "var(--green-hover)" }}
                          />
                        </g>
                      </g>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ══ REVIEWS — MASONRY ══ */}
      {testimonial?.status && testimonial?.list?.length ? (
        <section className="rev-sec" id="reviews">
          <div className="container">
            <div className="ey reveal">{lbl("testimonials", "Testimonials")}</div>
            <h2 className="sh mb-4 reveal">
              {testimonial.heading || "What students are saying"}
            </h2>
            <div className="rev-masonry reveal">
              {testimonial.list.map((t, i) => (
                <div className="rcard featured" key={i}>
                  <div className="rhead">
                    <div
                      className="rav"
                      style={{ background: "var(--s3)", color: "var(--li)" }}
                    >
                      {t.name
                        ? String(t.name).slice(0, 2).toUpperCase()
                        : "NA"}
                    </div>
                    <div>
                      <div className="rname">{t.name || "Anonymous"}</div>
                      <div className="rrole">{t.designation || "Student"}</div>
                      <div className="rstar">{stars(t.rating ?? 5)}</div>
                    </div>
                  </div>
                  <p className="rtext">"{t.review || ""}"</p>
                  {t.date ? <div className="rdate">{t.date}</div> : null}
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ══ FAQ ══ */}
      {faq?.status && faq?.list?.length ? (
        <section className="faq-sec" id="faq">
          <div className="container">
            <div className="ey reveal">{lbl("faq", "FAQ")}</div>
            <h2 className="sh mb-4 reveal">
              {faq.heading || "Frequently asked questions"}
            </h2>
            <div className="faq-cols reveal">
              <div>{faqLeft.map(renderFaqItem)}</div>
              <div>{faqRight.map(renderFaqItem)}</div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ══ CTA — LIME BACKGROUND ══ */}
      <section className="cta-sec">
        <div className="cta-noise" />
        <div className="cta-grid" />
        <div className="container">
          <div className="cta-inner align-items-center justify-content-center text-center">
            <h2 className="cta-title">
              {cLine1}
              <br />
              {cW1 !== undefined ? (
                <>
                  <span className="outline">{cW1}</span>
                  <br />
                </>
              ) : null}
              {cLine3}
            </h2>
            {heroSub ? <p className="cta-sub">{heroSub}</p> : null}
            <a href={enrollLink} className="btn-cta">
              <Icon.mortarboardFill /> {enrollText}
            </a>
            <div className="cta-note">
              <Icon.shieldCheck />
              <span>
                {lbl("money_back_guarantee", "30-Day Money-Back Guarantee.") +
                  " " +
                  lbl(
                    "not_satisfied",
                    "Not satisfied? Full refund, no questions asked"
                  )}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="foot">
        <div className="container">
          <div className="fbot">
            <div className="srow">
              {social.x_url ? (
                <a
                  href={social.x_url}
                  className="sic"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon.twitterX />
                </a>
              ) : null}
              {social.youtube_url ? (
                <a
                  href={social.youtube_url}
                  className="sic"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon.youtube />
                </a>
              ) : null}
              {social.linkedin_url ? (
                <a
                  href={social.linkedin_url}
                  className="sic"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon.linkedin />
                </a>
              ) : null}
              {social.instagram_url ? (
                <a
                  href={social.instagram_url}
                  className="sic"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon.instagram />
                </a>
              ) : null}
              {social.facebook_url ? (
                <a
                  href={social.facebook_url}
                  className="sic"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon.facebook />
                </a>
              ) : null}
            </div>
            {hasPrivacy || hasTerms ? (
              <div className="flinks">
                {hasPrivacy ? (
                  <button type="button" onClick={() => setOpenPolicy("privacy")}>
                    {policies.privacy_subject || "Privacy Policy"}
                  </button>
                ) : null}
                {hasTerms ? (
                  <button type="button" onClick={() => setOpenPolicy("terms")}>
                    {policies.terms_subject || "Terms"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </footer>

      <PolicyModal
        open={openPolicy}
        policies={policies}
        onClose={() => setOpenPolicy(null)}
      />
    </div>
  );
}
